import { Button } from "@/components/ui/button";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { CIFCalculator } from "./CIFCalculator";
import { CIF } from "@/constants/testIds";

export const Hero = ({ onEstimateClick, onWorkspaceClick }) => {
  return (
    <section
      id="top"
      data-testid={CIF.hero}
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden"
    >
      {/* backdrop layers */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-slate-50 via-white to-slate-50" />
      <div className="absolute inset-0 -z-10 bg-grid opacity-50" />
      <div className="absolute left-1/2 -translate-x-1/2 top-0 -z-10 h-[520px] w-[1100px] bg-gradient-to-b from-blue-50/80 via-transparent to-transparent blur-2xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left */}
          <div className="lg:col-span-5">
            <div data-testid={CIF.heroEyebrow} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-600 pulse-dot" />
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-slate-600">
                Workspace Capability
              </span>
              <span className="text-slate-300">·</span>
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-blue-700">
                Import Cost Estimation
              </span>
            </div>

            <h1
              data-testid={CIF.heroHeadline}
              className="font-display text-5xl sm:text-6xl lg:text-[5.2rem] font-medium leading-[1.02] tracking-[-0.035em] text-slate-900 mt-6"
            >
              Estimated CIF<sup className="text-2xl lg:text-3xl align-super text-blue-600 ml-1">™</sup>
            </h1>

            <p
              data-testid={CIF.heroSubheadline}
              className="font-display text-xl lg:text-2xl text-slate-700 mt-4 leading-snug tracking-tight"
            >
              Estimate your total import cost <span className="text-slate-900 underline decoration-blue-500/40 decoration-2 underline-offset-4">before you buy</span>.
            </p>

            <p
              data-testid={CIF.heroDescription}
              className="text-[15px] text-slate-500 leading-relaxed mt-5 max-w-lg"
            >
              Instantly estimate your landed cost by combining factory price,
              estimated freight and insurance — all before you issue your
              Purchase Order.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid={CIF.heroCtaPrimary}
                onClick={onEstimateClick}
                className="btn-shine h-12 rounded-full px-6 bg-slate-900 hover:bg-slate-800 text-white text-[14px] font-medium"
              >
                Estimate Your CIF <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button
                data-testid={CIF.heroCtaSecondary}
                variant="outline"
                onClick={onWorkspaceClick}
                className="h-12 rounded-full px-6 bg-white border-slate-200 hover:bg-slate-50 text-slate-900 text-[14px] font-medium"
              >
                Create Your Free Workspace <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Right - Calculator */}
          <div id="hero-calc" className="lg:col-span-7">
            <CIFCalculator />
          </div>
        </div>

        {/* Customer ticker */}
        <div className="mt-20 lg:mt-24 border-y border-slate-200/80 py-6">
          <div className="flex items-center gap-8 lg:gap-12 overflow-hidden">
            <span className="shrink-0 text-[10px] tracking-[0.22em] uppercase font-semibold text-slate-400">
              Trusted by importers
            </span>
            <div className="relative flex-1 overflow-hidden">
              <div className="flex w-max gap-12 animate-ticker">
                {[0, 1].flatMap((dup) =>
                  ["TATA", "OLAM", "COFCO", "WILMAR", "ADM", "BUNGE", "AMUL", "ITC", "MARS", "NESTLÉ"].map((b, i) => (
                    <span key={`${dup}-${b}-${i}`} className="font-display text-lg font-medium text-slate-300 tracking-tight whitespace-nowrap">
                      {b}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
